import { env } from "@/config/env"
import { configService } from "./api/config-service"
import { useConfigStore } from "./store/config-store"
import { detectConfigId, buildStrippedUrl } from "./domain/config-url"
import type { AppConfig } from "./types"

function cleanUrl(pathname: string, search: string, prefix: string): void {
  const next = buildStrippedUrl(pathname, search, prefix)
  if (next !== pathname + search) {
    window.history.replaceState(null, "", next)
  }
}

/**
 * Resolve the funnel config before the app renders:
 *   1. detect a `cfg_*` id in the path (or `?config=`)
 *   2. fetch it (once per id), persist to the session
 *   3. strip the id from the URL so the user lands on a clean start page
 *
 * Safe to await unconditionally — a no-op when there's no config in the URL.
 */
export async function bootstrapConfig(): Promise<void> {
  if (typeof window === "undefined") return

  const prefix = env.VITE_CONFIG_ID_PREFIX
  const { pathname, search } = window.location
  const detection = detectConfigId(pathname, search, prefix)

  if (!detection) return

  const store = useConfigStore.getState()

  // Same config already active this session → just clean the URL.
  if (store.config?.id === detection.id) {
    cleanUrl(pathname, search, prefix)
    return
  }

  store.setStatus("loading")

  try {
    const config: AppConfig = configService.isConfigured
      ? await configService.fetchConfig(detection.id)
      : { id: detection.id, url: null, params: {} }
    useConfigStore.getState().setConfig(config)
  } catch (err) {
    console.error("[config] failed to load", err)
    useConfigStore.getState().setStatus("error")
  } finally {
    cleanUrl(pathname, search, prefix)
  }
}
